import React, { useEffect, useMemo, useState } from 'react';
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Chip,
  Stack,
  Typography,
  Box,
  Button,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import HttpHelper from '../../utils/HttpHelper';
import FileDownload from '../../components/common/FileDownload';
import { NavLink } from 'react-router-dom';
import Filter, { DropdownSelect, CheckSelect, FilterState } from '../../components/Filter/Filter';
import { getJWT } from '../../utils/jwtMethods';
import { API_BASE_URL } from '../../utils/utils';
import { useLoading } from '../../contexts/LoadingContext';
import { formatAppDate } from '../../utils/dateFormat';

type ApplicantOverview = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  province: string | null;
  resumeAvailable: boolean;
  jobsApplied: string[];
  assessmentCompleted: boolean;
  immigrationStatus: string | null;
  lgbtIdentity: string | null;
  ageEligibility: string | null;
  workEligibility: string | null;
  genderIdentity: string | null;
  visibleMinority: string | null;
  indigenousPerson: string | null;
  disability: string | null;
  transcriptFileName: string | null;
  linkedIn: string | null;
  portfolio: string | null;
  github: string | null;
  publishedPapers: string | null;
  wageSubsidy: string | null;
  gpa: number | null;
  latestSubmissionDate: string | null;
  totalWorkExperienceYears: number;
  workExperienceDetails: string;
  jobStatus: number | null; // ApplicationStatus enum value
};

const AllApplicants: React.FC = () => {
  const [rows, setRows] = useState<ApplicantOverview[]>([]);
  const [allRows, setAllRows] = useState<ApplicantOverview[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [filterState, setFilterState] = useState<FilterState>({});
  const [availableProvinces, setAvailableProvinces] = useState<string[]>([]);
  const [availableJobs, setAvailableJobs] = useState<string[]>([]);
  const [availableImmigrationStatuses, setAvailableImmigrationStatuses] = useState<string[]>([]);
  const { showLoading, hideLoading } = useLoading();

  const fetchData = async () => {
    try {
      showLoading('Loading applicants data...');
      const res: any = await HttpHelper.get('profile/review/applicants-overview');
      if (res?.applicants) {
        setAllRows(res.applicants);
        setRows(res.applicants);

        // Extract unique values for filter options
        const provinces = Array.from(
          new Set(
            res.applicants
              .map((a: ApplicantOverview) => a.province)
              .filter((p: string | null) => !!p),
          ),
        ) as string[];

        const jobs = Array.from(
          new Set(
            res.applicants.flatMap((a: ApplicantOverview) => a.jobsApplied || []),
          ),
        ) as string[];

        const immigrationStatuses = Array.from(
          new Set(
            res.applicants
              .map((a: ApplicantOverview) => a.immigrationStatus)
              .filter((status: string | null) => !!status),
          ),
        ) as string[];

        setAvailableProvinces(provinces.sort());
        setAvailableJobs(jobs.sort());
        setAvailableImmigrationStatuses(immigrationStatuses.sort());
      }
    } catch (error) {
      console.error('Error loading applicants data:', error);
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply filters whenever filterState changes
  useEffect(() => {
    let filteredRows = [...allRows];

    // Filter by name (search box)
    if (filterState.title && typeof filterState.title === 'string') {
      const searchTerm = filterState.title.toLowerCase();
      filteredRows = filteredRows.filter(row => {
        const fullName = `${row.firstName || ''} ${row.lastName || ''}`.toLowerCase();
        return fullName.includes(searchTerm);
      });
    }

    // Filter by province
    if (filterState.province && filterState.province !== null && filterState.province !== -1) {
      filteredRows = filteredRows.filter(row => row.province === filterState.province);
    }

    // Filter by job applied
    if (filterState.jobApplied && filterState.jobApplied !== null && filterState.jobApplied !== -1) {
      filteredRows = filteredRows.filter(row =>
        row.jobsApplied && row.jobsApplied.includes(filterState.jobApplied as string)
      );
    }

    // Filter by immigration status
    if (filterState.immigrationStatus && filterState.immigrationStatus !== null && filterState.immigrationStatus !== -1) {
      filteredRows = filteredRows.filter(row => row.immigrationStatus === filterState.immigrationStatus);
    }

    // Filter by assessment completed (checkbox)
    if (filterState.assessmentCompleted === true) {
      filteredRows = filteredRows.filter(row => row.assessmentCompleted === true);
    }

    setRows(filteredRows);
    setPage(0); // Reset to first page when filters change
  }, [filterState, allRows]);

  const dropdownSelectData: DropdownSelect[] = useMemo(() => [
    {
      label: 'Province',
      labelId: 'province-select-label',
      categoryName: 'province',
      options: [
        { value: -1, text: 'All Provinces' },
        ...availableProvinces.map(p => ({ value: p, text: p }))
      ]
    },
    {
      label: 'Job Applied',
      labelId: 'job-select-label',
      categoryName: 'jobApplied',
      options: [
        { value: -1, text: 'All Jobs' },
        ...availableJobs.map(j => ({ value: j, text: j }))
      ]
    },
    {
      label: 'Immigration Status',
      labelId: 'immigration-select-label',
      categoryName: 'immigrationStatus',
      options: [
        { value: -1, text: 'All Statuses' },
        ...availableImmigrationStatuses.map(s => ({ value: s, text: s }))
      ]
    }
  ], [availableProvinces, availableJobs, availableImmigrationStatuses]);

  const checkSelectData: CheckSelect[] = [
    {
      label: 'Assessment Completed',
      categoryName: 'assessmentCompleted'
    }
  ];

  const pagedRows = useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rows, page, rowsPerPage],
  );

  const formatSubmissionDate = (dateString: string | null) => formatAppDate(dateString);

  const exportToCSV = () => {
    // CSV headers
    const headers = ['Applicant ID', 'Name', 'Email', 'Jobs Applied', 'Immigration Status', 'LGBTQIA2S+ Identity', 'Age Eligibility (15-30)', 'Work Eligibility', 'Gender Identity', 'Visible Minority', 'Indigenous Person', 'Disability', 'Transcript Available', 'LinkedIn', 'Portfolio', 'GitHub', 'Published Papers', 'Wage Subsidy', 'GPA', 'Yrs of Exp', 'Experience', 'Province', 'Assessment Completed', 'Submission Date'];

    // Convert rows to CSV format
    const csvRows = rows.map(row => {
      const applicantId = row.id.toString();
      const name = `${row.firstName || ''} ${row.lastName || ''}`.trim();
      const email = row.email || '';
      const jobsApplied = row.jobsApplied?.join('; ') || '';
      const immigrationStatus = row.immigrationStatus || 'N/A';
      const lgbtIdentity = row.lgbtIdentity || 'N/A';
      const ageEligibility = row.ageEligibility || 'N/A';
      const workEligibility = row.workEligibility || 'N/A';
      const genderIdentity = row.genderIdentity || 'N/A';
      const visibleMinority = row.visibleMinority || 'N/A';
      const indigenousPerson = row.indigenousPerson || 'N/A';
      const disability = row.disability || 'N/A';
      const gpa = row.gpa != null ? row.gpa.toString() : 'N/A';
      const workExperience = row.totalWorkExperienceYears != null && row.totalWorkExperienceYears > 0 ? row.totalWorkExperienceYears.toString() : 'N/A';
      const workExperienceDetails = row.workExperienceDetails || 'N/A';
      const province = row.province || '';
      const assessmentCompleted = row.assessmentCompleted ? 'Yes' : 'No';
      const transcriptAvailable = row.transcriptFileName ? 'Yes' : 'No';
      const linkedIn = row.linkedIn || 'N/A';
      const portfolio = row.portfolio || 'N/A';
      const github = row.github || 'N/A';
      const publishedPapers = row.publishedPapers || 'N/A';
      const wageSubsidy = row.wageSubsidy || 'N/A';
      const latestSubmission = formatSubmissionDate(row.latestSubmissionDate);

      // Escape fields that contain commas or quotes
      const escapeField = (field: string) => {
        if (field.includes(',') || field.includes('"') || field.includes('\n')) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      };

      return [applicantId, name, email, jobsApplied, immigrationStatus, lgbtIdentity, ageEligibility, workEligibility, genderIdentity, visibleMinority, indigenousPerson, disability, transcriptAvailable, linkedIn, portfolio, github, publishedPapers, wageSubsidy, gpa, workExperience, workExperienceDetails, province, assessmentCompleted, latestSubmission]
        .map(escapeField)
        .join(',');
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...csvRows].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `all_applicants_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAllResumes = async () => {
    try {
      // Get applicant IDs from filtered rows who have resumes
      const applicantIds = rows
        .filter(row => row.resumeAvailable)
        .map(row => row.id);

      if (applicantIds.length === 0) {
        alert('No resumes available to download from the current selection.');
        return;
      }

      // Create query string with applicant IDs
      const idsParam = applicantIds.join(',');
      const url = `/profile/review/applicants/resumes/download?ids=${idsParam}`;

      // Use the same pattern as FileDownload component
      fetch(`${API_BASE_URL}${url}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${getJWT()}`,
        },
      })
        .then(async response => {
          if (!response.ok) {
            throw new Error('Network response was not ok.');
          }

          const blob = await response.blob();
          const cd = response.headers.get('Content-Disposition');

          // Parse filename from Content-Disposition header
          let filename = 'resumes.zip';
          if (cd) {
            const filenameMatch = cd.match(/filename="([^"]+)"/i) || cd.match(/filename=([^;]+)/i);
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].trim();
            }
          }

          // Create download link
          const downloadUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = downloadUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(downloadUrl);
        })
        .catch(error => {
          console.error('Error downloading resumes:', error);
          alert('Failed to download resumes. Please try again.');
        });
    } catch (error) {
      console.error('Error preparing download:', error);
      alert('Failed to download resumes. Please try again.');
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      width: '100%',
      padding: '0',
      gap: 0.5,
      boxSizing: 'border-box'
    }}>
      {/* Filter sidebar */}
      <Box sx={{ width: '280px', flexShrink: 0 }}>
        <Filter
          DropdownSelectData={dropdownSelectData}
          CheckSelectData={checkSelectData}
          hasSearchBox={true}
          updateFilterState={setFilterState}
          useFixedPosition={false}
        />
      </Box>

      {/* Main content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ position: 'relative', mb: 1 }}>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{
              color: 'var(--turquoise-text)',
              textAlign: 'center',
              mt: -1,
              mb: 1
            }}
          >
            All Applicants
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
            <Button
              variant="contained"
              startIcon={<FolderZipIcon />}
              onClick={downloadAllResumes}
              sx={{
                backgroundColor: 'var(--ttg-black)',
                '&:hover': {
                  backgroundColor: 'var(--ttg-green)',
                },
              }}
            >
              Download All Resumes
            </Button>
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={exportToCSV}
              sx={{
                backgroundColor: 'var(--ttg-black)',
                '&:hover': {
                  backgroundColor: 'var(--ttg-green)',
                },
              }}
            >
              Export to CSV
            </Button>
          </Box>
        </Box>

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-root': { textAlign: 'center', padding: '2px 4px' } }}>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Jobs Applied</TableCell>
                  <TableCell>Immigration Status</TableCell>
                  <TableCell>LGBTQIA2S+ Identity</TableCell>
                  <TableCell>Age Eligibility (15-30)</TableCell>
                  <TableCell>Work Eligibility</TableCell>
                  <TableCell>Gender Identity</TableCell>
                  <TableCell>Visible Minority</TableCell>
                  <TableCell>Indigenous Person</TableCell>
                  <TableCell>Disability</TableCell>
                  <TableCell>Transcript</TableCell>
                  <TableCell>LinkedIn</TableCell>
                  <TableCell>Portfolio</TableCell>
                  <TableCell>GitHub</TableCell>
                  <TableCell>Papers</TableCell>
                  <TableCell>Wage Subsidy</TableCell>
                  <TableCell>GPA</TableCell>
                  <TableCell>Yrs of Exp</TableCell>
                  <TableCell>Experience</TableCell>
                  <TableCell>Resume</TableCell>
                  <TableCell>Province</TableCell>
                  <TableCell>Assessment Completed</TableCell>
                  <TableCell>Latest Submission</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map(a => (
                  <TableRow key={a.id} hover sx={{ '& .MuiTableCell-root': { textAlign: 'center', padding: '0px 4px' } }}>
                    <TableCell>
                      <NavLink to={`/profile-view/${a.id}`}>
                        {(a.firstName || '') + ' ' + (a.lastName || '')}
                      </NavLink>
                    </TableCell>
                    <TableCell>
                      {a.email || '—'}
                    </TableCell>
                    <TableCell>
                      {a.jobsApplied?.length ? (
                        <Stack direction="column" spacing={0.1} flexWrap="wrap">
                          {a.jobsApplied.map((j, idx) => (
                            <Chip key={`${a.id}-job-${idx}`} label={j} size="small" />
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {a.immigrationStatus || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {a.lgbtIdentity || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {a.ageEligibility || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {a.workEligibility || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {a.genderIdentity || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {a.visibleMinority || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {a.indigenousPerson || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {a.disability || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {a.transcriptFileName ? 'Available' : '—'}
                    </TableCell>
                    <TableCell>
                      {a.linkedIn || '—'}
                    </TableCell>
                    <TableCell>
                      {a.portfolio || '—'}
                    </TableCell>
                    <TableCell>
                      {a.github || '—'}
                    </TableCell>
                    <TableCell>
                      {a.publishedPapers || '—'}
                    </TableCell>
                    <TableCell>
                      {a.wageSubsidy || '—'}
                    </TableCell>
                    <TableCell>
                      {a.gpa || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {a.totalWorkExperienceYears != null && a.totalWorkExperienceYears > 0
                        ? a.totalWorkExperienceYears.toFixed(2)
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {a.workExperienceDetails || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <FileDownload
                        width="auto"
                        url={`/profile/review/applicants/${a.id}/resume/download`}
                        fileAvailable={a.resumeAvailable}
                        displayedText={a.resumeAvailable ? 'Download' : 'No Resume'}
                        centered={true}
                      />
                    </TableCell>
                    <TableCell>{a.province || '—'}</TableCell>
                    <TableCell>
                      {a.assessmentCompleted ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={a.latestSubmissionDate ? 'text.primary' : 'text.secondary'}>
                        {formatSubmissionDate(a.latestSubmissionDate)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default AllApplicants;
